import { useEffect } from 'react';
import { useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SpotResponseSchema, type Spot } from '@web/shared';
import { apiRequest, ApiRequestError } from '@/lib/api';

const KEY = 'nm.spot';

export interface CurrentSpot {
  spot: Spot;
  section?: string;
  appearanceBlurb?: string;
  checkedInAt: number;
}

let cache: CurrentSpot | null = read();
const listeners = new Set<() => void>();

function read(): CurrentSpot | null {
  try {
    const raw = localStorage.getItem(KEY);
    const parsed = raw ? (JSON.parse(raw) as CurrentSpot) : null;
    // Expire a stale check-in after 4 hours.
    if (parsed && Date.now() - parsed.checkedInAt > 4 * 3600_000) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function setCurrentSpot(next: CurrentSpot | null): void {
  try {
    if (next) localStorage.setItem(KEY, JSON.stringify(next));
    else localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
  cache = next;
  for (const l of listeners) l();
}

/** Fallback label for an ephemeral/ambient Spot, or no Spot at all. */
export const AMBIENT_SPOT_LABEL = 'Nearby';

/** A real venue's name is worth showing ("You're at The Grand"). An ephemeral
 * ambient Spot's stored name/code is an internal dedup key (`Open Circle ·
 * u4pruy`) — nobody should ever see that, so it reads as "Nearby" instead. */
export function spotLabel(spot: Spot | null | undefined): string {
  if (!spot || spot.ephemeral) return AMBIENT_SPOT_LABEL;
  return spot.name;
}

export function useCurrentSpot(): CurrentSpot | null {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => cache,
    () => cache,
  );
}

/**
 * Self-heal a stale `nm.spot`: the persisted Spot can vanish server-side — an
 * ephemeral Open Circle Spot gets cron-swept after a few hours, and a local
 * `db:reset` wipes everything. When that happens, drop the check-in so the UI
 * falls back to the friendly "pick your Spot" state instead of throwing
 * "Spot not found" on the next write. Call once on any page gated by the Spot.
 */
export function useEnsureCurrentSpotExists(): void {
  const current = useCurrentSpot();
  const spotId = current?.spot.id ?? null;
  const { error } = useQuery({
    queryKey: ['spot-exists', spotId],
    enabled: !!spotId,
    staleTime: 60_000,
    retry: false,
    queryFn: ({ signal }) =>
      apiRequest(`/api/spots/${spotId}`, { schema: SpotResponseSchema, signal }),
  });

  useEffect(() => {
    if (error instanceof ApiRequestError && error.status === 404) {
      setCurrentSpot(null);
    }
  }, [error]);
}
